import React, { useEffect, useState } from 'react';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ReportDA } from '../../../../services/admin/report.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { IncomeStatement } from '../../../../models/report.model';
import { FiscalYearLike, resolvePreviousFiscalYear, previousPeriodBounds } from '../../../../scripts/fiscal.year.previous.script';
import { formatMoney, amountClass, tabularStyle } from '../../../../scripts/format.value.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { dateFormat, dateFormatLocalized } from '../../../../scripts/date.time.format.script';
import { triggerBlobDownload } from '../../../../scripts/export.blob.script';
import { exportFallbackFilename } from '../../../../scripts/document.filename.script';
import { GlobalSwal } from '../../../../styles/swal.style';

import AppPreloader from '../../../../components/Preloader';
import AppReportTree from '../../../../components/element/ReportTree';
import DropdownActions from '../../../../components/dropdown/DropdownActions';

export interface IncomeStatementPageProps {};

const IncomeStatementPage: React.FunctionComponent<IncomeStatementPageProps> = props => {
  const {setRoute, companyForUser, accountingAccountsForCompany} = UseOutletContextAccounting()
  const {lang} = StateLanguage()

  const [fiscalYears, setFiscalYears] = useState<FiscalYearLike[]>([])
  const [selectedFY, setSelectedFY] = useState<FiscalYearLike | null>(null)
  const [report, setReport] = useState<IncomeStatement | null>(null)
  const [reportPrev, setReportPrev] = useState<IncomeStatement | null>(null)
  const [previousBounds, setPreviousBounds] = useState<{ from: string; to: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showZero, setShowZero] = useState<boolean>(false)

  const locale = (lang.code ?? 'fr').substring(0, 2).toLowerCase()

  const comparisonLabel = (() => {
    if (!previousBounds) return ''
    const from = previousBounds.from
    const to = previousBounds.to
    // Calendar year check: from = YYYY-01-01, to = YYYY-12-31, same year
    const isCalendar = from.endsWith('-01-01') && to.endsWith('-12-31') && from.substring(0, 4) === to.substring(0, 4)
    if (isCalendar) {
      return `${lang.labels.fiscalYear} ${to.substring(0, 4)}`
    }
    return `${dateFormatLocalized(lang.code, from)}–${dateFormatLocalized(lang.code, to)}`
  })()

  useEffect(() => {
    setRoute && setRoute({path: {root: lang.labels.accounting, branch: lang.labels.incomeStatement}, company: false})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser) {
      loadFiscalYearsAndInit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    if (companyForUser && selectedFY && fiscalYears.length > 0) {
      loadReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY])

  const loadFiscalYearsAndInit = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id) return

    try {
      const response = await FiscalYearDA.getFiscalYears(id)
      if (response.status === 200) {
        const data = response.data
        const items: FiscalYearLike[] = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        setFiscalYears(items)

        if (items.length > 0) {
          const today = new Date()
          const current = items.find(fy => new Date(fy.start_date) <= today && new Date(fy.end_date) >= today)
            ?? items.sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0]

          setSelectedFY(current)
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const loadReport = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || !selectedFY) return

    const from = selectedFY.start_date.substring(0, 10)
    const to = selectedFY.end_date.substring(0, 10)

    setLoading(true)
    setReport(null)
    setReportPrev(null)
    setPreviousBounds(null)

    try {
      const response = await ReportDA.getIncomeStatement(id, from, to)
      if (response.status === 200) {
        setReport(response.data as IncomeStatement)
      } else {
        setReport(null)
      }

      // Load N-1
      if (fiscalYears.length > 0) {
        const prev = resolvePreviousFiscalYear(fiscalYears, { start_date: selectedFY.start_date })
        if (prev) {
          const bounds = previousPeriodBounds(prev)
          const prevFrom = bounds.from.substring(0, 10)
          const prevTo = bounds.to.substring(0, 10)
          setPreviousBounds({ from: prevFrom, to: prevTo })
          const prevResponse = await ReportDA.getIncomeStatement(id, prevFrom, prevTo)
          if (prevResponse.status === 200) {
            setReportPrev(prevResponse.data as IncomeStatement)
          }
        }
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  const handleFYChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const fyId = event.target.value
    const fy = fiscalYears.find(f => (f.id ?? f._id) === fyId)
    if (fy) setSelectedFY(fy)
  }

  const selectedFYId = selectedFY?.id ?? selectedFY?._id ?? ''

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || !selectedFY || exporting) return
    setExporting(true)
    try {
      const from = selectedFY.start_date.substring(0, 10)
      const to = selectedFY.end_date.substring(0, 10)
      const period = `${from}_${to}`
      const fallback = exportFallbackFilename(lang.labels.incomeStatement, companyForUser?.name ?? '', period, format)
      const result = await ReportDA.exportIncomeStatement(id, format, {
        from,
        to,
        previous_from: previousBounds?.from || undefined,
        previous_to: previousBounds?.to || undefined,
        hide_zero: !showZero,
        lang: lang.code.toLowerCase(),
      }, fallback)
      if (result.blob && result.filename) {
        triggerBlobDownload(result.blob, result.filename)
      } else {
        GlobalSwal.fire({ title: result.error || lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
      }
    } catch (error) {
      console.error('[IncomeStatement] export failed', error)
      GlobalSwal.fire({ title: lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="w-100 h-100">
      { companyForUser && selectedFY
        ?
        <>
          <div className="card mb-5">
            <div className="card-header">
              <div className="card-title d-flex align-items-center gap-3">
                <h2 className="mb-0">{lang.labels.incomeStatement}</h2>
                { report?.interim === true
                  ? <span className="badge badge-light-warning fs-7 px-4 py-2">{lang.labels.interim}</span>
                  : report?.provisional === true
                    ? <span className="badge badge-light-warning fs-7 px-4 py-2">{lang.labels.provisional}</span>
                    : null
                }
              </div>
              <div className="card-toolbar">
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={() => setShowZero(!showZero)}
                  >
                    {showZero ? lang.labels.hideZeroAccounts : lang.labels.showZeroAccounts}
                  </button>
                  <div className="d-flex align-items-center">
                    <label className="fw-bold me-3 text-nowrap">{lang.labels.period}:</label>
                    <select
                      className="form-select w-250px"
                      value={selectedFYId}
                      onChange={handleFYChange}
                    >
                      { fiscalYears.map((fy) => {
                        const fyId = fy.id ?? fy._id ?? ''
                        return (
                          <option key={fyId} value={fyId}>
                            {dateFormat(lang.code, fy.start_date)} – {dateFormat(lang.code, fy.end_date)}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                  <DropdownActions
                    label={exporting ? `${lang.labels.export}...` : lang.labels.export}
                    buttonClassName={`btn btn-sm ${loading || exporting ? 'btn-light disabled' : 'btn-light btn-active-light-primary'}`}
                    items={[
                      { key: 'xlsx', label: `${lang.labels.excel} (.xlsx)`, onClick: () => handleExport('xlsx') },
                      { key: 'pdf', label: `${lang.labels.pdf} (.pdf)`, onClick: () => handleExport('pdf') },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>

          { loading
            ?
            <AppPreloader />
            :
            report
            ?
            <>
              <div className="card mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.revenue}</h4>
                  </div>
                </div>
                <div className="card-body">
                  <AppReportTree
                    groups={report.revenue.groups}
                    groupsPrevious={reportPrev?.revenue?.groups}
                    locale={locale}
                    showZero={showZero}
                    accounts={accountingAccountsForCompany}
                    previousLabel={comparisonLabel}
                  />
                  <div className="border-top mt-3 pt-3"></div>
                  <div className="d-flex justify-content-between fw-bolder fs-5">
                    <span>{lang.labels.total} {lang.labels.revenue}</span>
                    <div className="d-flex">
                      <span className={`text-end ${amountClass(report.revenue.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.revenue.total)}</span>
                      { reportPrev &&
                        <>
                          <span className={`text-end ${amountClass(reportPrev.revenue.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.revenue.total)}</span>
                          <span className={`text-end ${amountClass(report.revenue.total - reportPrev.revenue.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                            {((report.revenue.total - reportPrev.revenue.total) >= 0 ? '+' : '')}{formatMoney(report.revenue.total - reportPrev.revenue.total)}
                          </span>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.expenses}</h4>
                  </div>
                </div>
                <div className="card-body">
                  <AppReportTree
                    groups={report.expenses.groups}
                    groupsPrevious={reportPrev?.expenses?.groups}
                    locale={locale}
                    showZero={showZero}
                    accounts={accountingAccountsForCompany}
                    previousLabel={comparisonLabel}
                  />
                  <div className="border-top mt-3 pt-3"></div>
                  <div className="d-flex justify-content-between fw-bolder fs-5">
                    <span>{lang.labels.total} {lang.labels.expenses}</span>
                    <div className="d-flex">
                      <span className={`text-end ${amountClass(report.expenses.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.expenses.total)}</span>
                      { reportPrev &&
                        <>
                          <span className={`text-end ${amountClass(reportPrev.expenses.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.expenses.total)}</span>
                          <span className={`text-end ${amountClass(report.expenses.total - reportPrev.expenses.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                            {((report.expenses.total - reportPrev.expenses.total) >= 0 ? '+' : '')}{formatMoney(report.expenses.total - reportPrev.expenses.total)}
                          </span>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>

              { report.ancillary_activities && report.ancillary_activities.groups.length > 0 &&
                <div className="card mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.ancillaryActivities}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <AppReportTree
                      groups={report.ancillary_activities.groups}
                      groupsPrevious={reportPrev?.ancillary_activities?.groups}
                      locale={locale}
                      showZero={showZero}
                      accounts={accountingAccountsForCompany}
                      previousLabel={comparisonLabel}
                    />
                    <div className="border-top mt-3 pt-3"></div>
                    <div className="d-flex justify-content-between fw-bolder fs-5">
                      <span>{lang.labels.total} {lang.labels.ancillaryActivities}</span>
                      <div className="d-flex">
                        <span className={`text-end ${amountClass(report.ancillary_activities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.ancillary_activities.total)}</span>
                        { reportPrev?.ancillary_activities &&
                          <>
                            <span className={`text-end ${amountClass(reportPrev.ancillary_activities.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.ancillary_activities.total)}</span>
                            <span className={`text-end ${amountClass(report.ancillary_activities.total - reportPrev.ancillary_activities.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                              {((report.ancillary_activities.total - reportPrev.ancillary_activities.total) >= 0 ? '+' : '')}{formatMoney(report.ancillary_activities.total - reportPrev.ancillary_activities.total)}
                            </span>
                          </>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }

              { report.extraordinary && report.extraordinary.groups.length > 0 &&
                <div className="card mb-5">
                  <div className="card-header">
                    <div className="card-title">
                      <h4>{lang.labels.extraordinaryItems}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <AppReportTree
                      groups={report.extraordinary.groups}
                      groupsPrevious={reportPrev?.extraordinary?.groups}
                      locale={locale}
                      showZero={showZero}
                      accounts={accountingAccountsForCompany}
                      previousLabel={comparisonLabel}
                    />
                    <div className="border-top mt-3 pt-3"></div>
                    <div className="d-flex justify-content-between fw-bolder fs-5">
                      <span>{lang.labels.total} {lang.labels.extraordinaryItems}</span>
                      <div className="d-flex">
                        <span className={`text-end ${amountClass(report.extraordinary.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(report.extraordinary.total)}</span>
                        { reportPrev?.extraordinary &&
                          <>
                            <span className={`text-end ${amountClass(reportPrev.extraordinary.total)}`} style={{ ...tabularStyle, minWidth: 120 }}>{formatMoney(reportPrev.extraordinary.total)}</span>
                            <span className={`text-end ${amountClass(report.extraordinary.total - reportPrev.extraordinary.total)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                              {((report.extraordinary.total - reportPrev.extraordinary.total) >= 0 ? '+' : '')}{formatMoney(report.extraordinary.total - reportPrev.extraordinary.total)}
                            </span>
                          </>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }

              <div className="card mb-5">
                <div className="card-body border-top">
                  <div className="d-flex justify-content-between fw-bolder fs-4">
                    <span>{lang.labels.netResult}</span>
                    <div className="d-flex">
                      <span className={`text-end ${report.net_result >= 0 ? 'text-success' : 'text-danger'}`} style={{ ...tabularStyle, minWidth: 120 }}>
                        {formatMoney(report.net_result)}
                      </span>
                      { reportPrev &&
                        <>
                          <span className={`text-end ${reportPrev.net_result >= 0 ? 'text-success' : 'text-danger'}`} style={{ ...tabularStyle, minWidth: 120 }}>
                            {formatMoney(reportPrev.net_result)}
                          </span>
                          <span className={`text-end ${amountClass(report.net_result - reportPrev.net_result)}`} style={{ ...tabularStyle, minWidth: 100 }}>
                            {((report.net_result - reportPrev.net_result) >= 0 ? '+' : '')}{formatMoney(report.net_result - reportPrev.net_result)}
                          </span>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </>
            :
            <div className="card">
              <div className="card-body text-center text-muted py-10">
                {lang.labels.noRecordsFound}
              </div>
            </div>
          }
        </>
        :
        <AppPreloader />
      }
    </div>
  )
};

export default IncomeStatementPage;
